import * as RadixAvatar from '@radix-ui/react-avatar'
import React, { useRef, useState } from 'react'
import { useUnmount } from 'react-use'
import styled, { css } from 'styled-components'

import { useImgCached, type ClassNameProps } from '@root/shared/react'

type RetryProps = {
  /**
   * Retry delay in seconds
   */
  retryDelay?: number
  retryMaxCount?: number
}

export type AvatarProps = {
  fallback?: string | React.ReactElement
  src?: string
  alt?: string
  size?: number
  color?: string
} & ClassNameProps &
  RetryProps &
  React.HTMLAttributes<HTMLElement>

export const Avatar: React.FC<AvatarProps> = ({
  fallback,
  src,
  alt,
  size = 40,
  color = 'gray',
  className,
  retryDelay,
  retryMaxCount = Infinity,
  ...divProps
}) => {
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef<number | undefined>(undefined)
  // This is a hack to let radix avatar reset state and switch it's internal state back to success
  const [retryKey, setRetryKey] = useState('initial')
  const isCached = useImgCached(src)
  const [isLoaded, setIsLoaded] = useState(isCached)

  const handleRetry = () => {
    if (!src || !retryDelay || retryCountRef.current > retryMaxCount) {
      return
    }

    const retry = () => {
      const img = new Image()
      img.onerror = handleRetry
      img.onload = () => {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current)
        }
        setRetryKey('success')
      }
      img.src = src
    }

    retryTimeoutRef.current = window.setTimeout(() => {
      retry()
    }, retryDelay * 1000)
  }

  const handleStateChange: RadixAvatar.AvatarImageProps['onLoadingStatusChange'] =
    (status) => {
      if (status === 'error') {
        handleRetry()
      }
      if (status === 'loaded') {
        setIsLoaded(true)
      }
    }

  useUnmount(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
  })

  return (
    <Root
      className={className}
      css={css`
        height: ${size}px;
        width: ${size}px;
        font-size: ${size * 0.4}px;

        ${divProps.onClick && `cursor: pointer;`}
      `}
      {...divProps}
    >
      {fallback ? <Fallback $color={color}>{fallback}</Fallback> : undefined}
      <AvatarImageWrapper $isLoaded={isLoaded}>
        <AvatarImage
          role="img"
          key={retryKey}
          src={src}
          alt={alt}
          loading="lazy"
          height={size}
          width={size}
          onLoadingStatusChange={handleStateChange}
        />
      </AvatarImageWrapper>
    </Root>
  )
}

const Root = styled(RadixAvatar.Avatar)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  user-select: none;
  border-radius: 50%;
`

const AvatarImage = styled(RadixAvatar.AvatarImage)`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`
const AvatarImageWrapper = styled.div<{ $isLoaded: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  transition: all ease 0.3s;
  opacity: ${(props) => (props.$isLoaded ? 1 : 0)};
`

const Fallback = styled.div.attrs<{ $color: string }>((props) => ({
  style: {
    background: props.$color,
  },
}))`
  position: absolute;
  left: 1px;
  right: 1px;
  top: 1px;
  bottom: 1px;
  border-radius: inherit;

  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-12);
  font-weight: 500;
`
